<template>
  <v-dialog
    :value="value"
    max-width="500"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        {{ isEdit ? '管理設定' : '対象に追加' }}
      </v-card-title>

      <v-card-text>
        <v-form
          ref="form"
          class="pt-4"
        >
          <!-- 商品選択（新規のみ） -->
          <v-autocomplete
            v-if="!isEdit"
            v-model="form.itemId"
            :items="items"
            item-text="name"
            item-value="id"
            label="商品 *"
            :rules="[rules.required]"
            outlined
            dense
            class="mb-2"
          />

          <!-- 商品名表示（編集時） -->
          <v-text-field
            v-else
            :value="editItem ? editItem.itemName : ''"
            label="商品"
            outlined
            dense
            disabled
            class="mb-2"
          />

          <!-- 単位種別 -->
          <div class="mb-8">
            <div class="text-subtitle-2 mb-2">
              単位種別 *
            </div>
            <v-radio-group
              v-model="form.unitType"
              row
              hide-details
              class="mt-0 pt-0"
            >
              <v-radio
                label="個数管理"
                value="piece"
              />
              <v-radio
                label="杯数管理"
                value="bottle"
              />
            </v-radio-group>
          </div>

          <!-- 杯数管理の場合 -->
          <v-card
            v-if="form.unitType === 'bottle'"
            outlined
            class="mb-8 pa-4"
          >
            <v-text-field
              v-model.number="form.bottleCapacityMl"
              label="ボトル容量(ml) *"
              type="number"
              step="1"
              min="1"
              :rules="[rules.required, rules.positiveInteger]"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
            />

            <v-text-field
              v-model.number="form.servingSizeMl"
              label="1杯あたり消費量(ml) *"
              type="number"
              step="1"
              min="1"
              :rules="[rules.required, rules.positiveInteger, rules.servingSizeValid]"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
            />

            <div class="text-body-2">
              1本あたり <strong>{{ calculatedServings || 0 }}</strong> 杯
            </div>
          </v-card>

          <!-- 現在在庫（新規のみ） -->
          <template v-if="!isEdit">
            <!-- 個数管理の場合 -->
            <v-text-field
              v-if="form.unitType === 'piece'"
              v-model.number="form.currentQuantity"
              label="現在在庫 *"
              type="number"
              step="1"
              min="0"
              :rules="[rules.required, rules.nonNegativeInteger]"
              outlined
              dense
              suffix="個"
              hint="現在の在庫数量"
              persistent-hint
              class="mb-4"
            />
            <!-- 杯数管理の場合: ボトル本数で入力 -->
            <v-text-field
              v-else
              v-model.number="form.currentBottles"
              label="現在在庫 *"
              type="number"
              step="1"
              min="0"
              :rules="[rules.required, rules.nonNegativeInteger]"
              outlined
              dense
              suffix="本"
              :hint="calculatedServings ? `→ ${form.currentBottles * calculatedServings}杯` : '現在の在庫数量'"
              persistent-hint
              class="mb-4"
            />
          </template>

          <!-- 基準在庫 -->
          <!-- 個数管理の場合 -->
          <v-text-field
            v-if="form.unitType === 'piece'"
            v-model.number="form.standardQuantity"
            label="基準在庫"
            type="number"
            step="1"
            min="0"
            outlined
            dense
            suffix="個"
            hint="通常ストックしている数量"
            persistent-hint
          />
          <!-- 杯数管理の場合: ボトル本数で入力 -->
          <v-text-field
            v-else
            v-model.number="form.standardBottles"
            label="基準在庫"
            type="number"
            step="1"
            min="0"
            outlined
            dense
            suffix="本"
            :hint="calculatedServings ? `→ ${form.standardBottles * calculatedServings}杯` : '通常ストックしている数量'"
            persistent-hint
          />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="close"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :loading="isSaving"
          @click="save"
        >
          保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import InventoryItemApi from '@/api/v2/inventory/InventoryItem'

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editItem: {
      type: Object,
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const form = reactive({
      itemId: null,
      unitType: 'piece',
      currentQuantity: 0,
      standardQuantity: null,
      bottleCapacityMl: null,
      servingSizeMl: null,
      // 杯数管理用: ボトル本数で入力
      currentBottles: 0,
      standardBottles: null,
    })

    const isSaving = ref(false)

    const isEdit = computed(() => !!props.editItem)

    // 杯数の自動計算
    const calculatedServings = computed(() => {
      if (form.unitType !== 'bottle') return null
      if (!form.bottleCapacityMl || !form.servingSizeMl) return null

      return Math.floor(form.bottleCapacityMl / form.servingSizeMl)
    })

    // 杯数管理の場合、ボトル容量・消費量が変わったらデフォルト1本に設定
    watch(calculatedServings, newVal => {
      if (form.unitType === 'bottle' && newVal !== null && !isEdit.value) {
        // 新規登録時のみデフォルト1本に設定
        if (form.currentBottles === 0) {
          form.currentBottles = 1
        }
        if (form.standardBottles === null) {
          form.standardBottles = 1
        }
      }
    })

    // バリデーションルール
    const rules = {
      required: v => (v !== null && v !== '' && v !== undefined) || '必須項目です',
      positiveInteger: v => {
        if (v === null || v === '') return true
        const num = Number(v)

        return (Number.isInteger(num) && num > 0) || '1以上の整数を入力してください'
      },
      nonNegativeInteger: v => {
        if (v === null || v === '') return true
        const num = Number(v)

        return (Number.isInteger(num) && num >= 0) || '0以上の整数を入力してください'
      },
      servingSizeValid: v => {
        if (v === null || v === '') return true
        if (!form.bottleCapacityMl) return true

        return v <= form.bottleCapacityMl || 'ボトル容量以下の値を入力してください'
      },
    }

    // ダイアログ開閉時のフォームリセット
    watch(() => props.value, newVal => {
      if (newVal) {
        if (props.editItem) {
          // 編集モード
          form.itemId = props.editItem.itemId
          form.unitType = props.editItem.unitType
          form.bottleCapacityMl = props.editItem.bottleCapacityMl
          form.servingSizeMl = props.editItem.servingSizeMl

          // 杯数管理の場合、杯数からボトル本数を計算
          if (props.editItem.unitType === 'bottle') {
            const servingsPerBottle = form.bottleCapacityMl && form.servingSizeMl
              ? Math.floor(form.bottleCapacityMl / form.servingSizeMl)
              : 1
            form.standardBottles = props.editItem.standardQuantity != null
              ? Math.floor(props.editItem.standardQuantity / servingsPerBottle)
              : null
          } else {
            form.standardQuantity = props.editItem.standardQuantity
          }
        } else {
          // 新規モード
          form.itemId = null
          form.unitType = 'piece'
          form.currentQuantity = 0
          form.standardQuantity = null
          form.bottleCapacityMl = null
          form.servingSizeMl = null
          form.currentBottles = 0
          form.standardBottles = null
        }
      }
    }, { immediate: true })

    const close = () => {
      emit('input', false)
    }

    const save = async () => {
      if (!vm.$refs.form.validate()) return

      isSaving.value = true

      try {
        let standardQty
        let currentQty

        if (form.unitType === 'bottle') {
          // 杯数管理: ボトル本数 × 1本あたり杯数 = 杯数
          const servingsPerBottle = calculatedServings.value || 0
          standardQty = form.standardBottles != null ? form.standardBottles * servingsPerBottle : null
          currentQty = (form.currentBottles || 0) * servingsPerBottle
        } else {
          // 個数管理: そのまま
          standardQty = form.standardQuantity
          currentQty = form.currentQuantity
        }

        const data = {
          unitType: form.unitType,
          standardQuantity: standardQty,
          bottleCapacityMl: form.unitType === 'bottle' ? form.bottleCapacityMl : null,
          servingSizeMl: form.unitType === 'bottle' ? form.servingSizeMl : null,
        }

        if (isEdit.value) {
          await InventoryItemApi.update(props.editItem.id, data)
          vm.$toast.success('更新しました')
        } else {
          data.itemId = form.itemId
          data.currentQuantity = currentQty
          await InventoryItemApi.create(data)
          vm.$toast.success('対象にしました')
        }

        emit('saved')
        close()
      } catch (error) {
        const message = error?.response?.data?.message || '保存に失敗しました'
        vm.$toast.error(message)
      } finally {
        isSaving.value = false
      }
    }

    return {
      form,
      isSaving,
      isEdit,
      calculatedServings,
      rules,
      close,
      save,
    }
  },
}
</script>
